/**
 * MobileMissionsSuivisList
 *
 * Mobile card-based replacement for MissionsSuivisDataTable.
 * Shows each mission followup as a touch-friendly card.
 * Supports search, pagination, and tap-to-view details.
 */
import React, { useState, useMemo, useCallback, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  CardActionArea,
  TextField,
  InputAdornment,
  Chip,
  Typography,
  Pagination,
  Fade,
  keyframes,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ChatBubbleOutlineRoundedIcon from "@mui/icons-material/ChatBubbleOutlineRounded";
import CalendarTodayRoundedIcon from "@mui/icons-material/CalendarTodayRounded";
import PersonOutlineRoundedIcon from "@mui/icons-material/PersonOutlineRounded";
import moment from "moment";
import Langs, { Localize } from "../../../app/lang/langs";
import { getTagClass } from "../../../app/utils/helpers";
import { formatDate } from "../contractsTableUtils";

/* ------------------------------------------------------------------ */
/*  Constants & Animations                                             */
/* ------------------------------------------------------------------ */
const TRANSITION_SMOOTH = "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)";
const PAGE_SIZE = 20;

const slideUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(16px) scale(0.97);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
`;

const fadeInScale = keyframes`
  from {
    opacity: 0;
    transform: scale(0.92);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

const shimmer = keyframes`
  0% { background-position: -200% center; }
  100% { background-position: 200% center; }
`;

const severityToColor = {
  success: "success",
  info: "info",
  warning: "warning",
  danger: "error",
  null: "default",
  undefined: "default",
};

/* Status color mapping for gradient accents */
const statusGradients = {
  success: "linear-gradient(135deg, rgba(58,156,96,0.15), rgba(58,156,96,0.05))",
  info: "linear-gradient(135deg, rgba(0,103,184,0.15), rgba(0,103,184,0.05))",
  warning: "linear-gradient(135deg, rgba(255,144,21,0.15), rgba(255,144,21,0.05))",
  error: "linear-gradient(135deg, rgba(251,105,98,0.15), rgba(251,105,98,0.05))",
  default: "linear-gradient(135deg, rgba(0,0,0,0.06), rgba(0,0,0,0.02))",
};

const statusAccentColors = {
  success: "rgba(58,156,96,0.6)",
  info: "rgba(0,103,184,0.6)",
  warning: "rgba(255,144,21,0.6)",
  error: "rgba(251,105,98,0.6)",
  default: "rgba(0,0,0,0.15)",
};

/**
 * @param {Object} props
 * @param {Array}  props.data - allMissionsFollowups
 * @param {Function} props.onCardTap - called with followup data when tapped
 * @param {Function} props.onValueChange - called with filtered data for export
 * @param {Function} props.onTeamTap - navigate to team page
 */
export default function MobileMissionsSuivisList({
  data,
  onCardTap,
  onValueChange,
  onTeamTap,
}) {
  const [searchText, setSearchText] = useState("");
  const [page, setPage] = useState(1);

  const baseData = data || [];

  // Filter by search
  const filteredData = useMemo(() => {
    if (!searchText.trim()) return baseData;
    const terms = searchText.toLowerCase().split(" ").filter(Boolean);
    return baseData.filter((item) => {
      const searchStr = [
        item.nom,
        item.prenom,
        item.status,
        item.type,
        item.client,
        item.current_team_name,
        item.comments,
        item.createdBy,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return terms.every((t) => searchStr.includes(t));
    });
  }, [baseData, searchText]);

  // Notify parent
  const notifyParent = useCallback(() => {
    if (onValueChange) onValueChange(filteredData);
  }, [filteredData, onValueChange]);

  useEffect(() => {
    notifyParent();
  }, [notifyParent]);

  useEffect(() => {
    setPage(1);
  }, [searchText]);

  const totalPages = Math.ceil(filteredData.length / PAGE_SIZE);
  const paginatedData = filteredData.slice(
    (page - 1) * PAGE_SIZE,
    page * PAGE_SIZE
  );

  const getChipColor = (value) => {
    const severity = getTagClass(value);
    return severityToColor[severity] || "default";
  };

  const getStatusAccent = (value) => {
    const severity = getTagClass(value);
    return statusAccentColors[severity] || statusAccentColors.default;
  };

  const getStatusGradient = (value) => {
    const severity = getTagClass(value);
    return statusGradients[severity] || statusGradients.default;
  };

  return (
    <Box
      sx={{
        px: 2,
        pt: 1.5,
        pb: 2.5,
        mt: "130px",
        height: "calc(100vh - 130px)",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Search bar — glassmorphic with icon glow */}
      <Box
        sx={{
          mb: 1.5,
          animation: `${fadeInScale} 0.4s cubic-bezier(0, 0, 0.2, 1) both`,
        }}
      >
        <TextField
          fullWidth
          size="small"
          placeholder={Localize("Search") + "..."}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon
                  fontSize="small"
                  sx={{
                    color: searchText
                      ? "rgb(255,144,21)"
                      : "rgba(0,40,128,0.4)",
                    transition: TRANSITION_SMOOTH,
                  }}
                />
              </InputAdornment>
            ),
          }}
          sx={{
            "& input": {
              fontSize: "16px",
              fontWeight: 400,
              letterSpacing: "0.01em",
              "&::placeholder": {
                opacity: 0.45,
                fontWeight: 300,
              },
            },
            "& .MuiOutlinedInput-root": {
              borderRadius: "16px",
              bgcolor: "rgba(255,255,255,0.8)",
              backdropFilter: "blur(16px)",
              WebkitBackdropFilter: "blur(16px)",
              transition: TRANSITION_SMOOTH,
              boxShadow:
                "0 2px 12px rgba(0,40,128,0.04), 0 1px 4px rgba(0,0,0,0.03)",
              "& fieldset": {
                borderColor: "rgba(0,40,128,0.08)",
                borderWidth: "1.5px",
                transition: TRANSITION_SMOOTH,
              },
              "&:hover fieldset": {
                borderColor: "rgba(0,40,128,0.18)",
              },
              "&.Mui-focused": {
                bgcolor: "rgba(255,255,255,0.95)",
                boxShadow:
                  "0 4px 20px rgba(255,144,21,0.12), 0 0 0 3px rgba(255,144,21,0.1)",
              },
              "&.Mui-focused fieldset": {
                borderColor: "rgb(255,144,21)",
                borderWidth: "1.5px",
              },
            },
          }}
        />
      </Box>

      {/* Results count — with subtle animation */}
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{
          mb: 1,
          px: 0.5,
          fontWeight: 500,
          letterSpacing: "0.03em",
          opacity: 0.6,
          fontSize: "0.72rem",
          textTransform: "uppercase",
          animation: `${fadeInScale} 0.5s cubic-bezier(0, 0, 0.2, 1) 0.1s both`,
        }}
      >
        {filteredData.length} {Localize("results")}
      </Typography>

      {/* Card list */}
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          WebkitOverflowScrolling: "touch",
          pb: 2,
          scrollBehavior: "smooth",
          "&::-webkit-scrollbar": { width: 5 },
          "&::-webkit-scrollbar-track": {
            background: "transparent",
            borderRadius: 3,
          },
          "&::-webkit-scrollbar-thumb": {
            background:
              "linear-gradient(180deg, rgba(255,144,21,0.2), rgba(0,40,128,0.15))",
            borderRadius: 3,
            "&:hover": {
              background:
                "linear-gradient(180deg, rgba(255,144,21,0.35), rgba(0,40,128,0.25))",
            },
          },
        }}
      >
        {paginatedData.map((item, index) => {
          const chipColor = getChipColor(item.status);
          const accentColor = getStatusAccent(item.status);
          const bgGradient = getStatusGradient(item.status);

          return (
            <Box
              key={item.id}
              sx={{
                animation: `${slideUp} 0.45s cubic-bezier(0, 0, 0.2, 1) ${
                  index * 0.04
                }s both`,
              }}
            >
              <Card
                elevation={0}
                sx={{
                  mb: 1.5,
                  borderRadius: "18px",
                  overflow: "hidden",
                  position: "relative",
                  bgcolor: "rgba(255,255,255,0.88)",
                  backdropFilter: "blur(20px)",
                  WebkitBackdropFilter: "blur(20px)",
                  border: "1px solid rgba(255,255,255,0.5)",
                  boxShadow: `
                    0 1px 2px rgba(0,0,0,0.03),
                    0 4px 12px rgba(0,40,128,0.04),
                    0 8px 24px rgba(0,0,0,0.03)
                  `,
                  transition:
                    "transform 300ms cubic-bezier(0.4, 0, 0.2, 1), box-shadow 300ms cubic-bezier(0.4, 0, 0.2, 1), border-color 300ms cubic-bezier(0.4, 0, 0.2, 1)",
                  "&:active": {
                    transform: "scale(0.975)",
                    boxShadow: "0 1px 6px rgba(0,40,128,0.06)",
                    borderColor: "rgba(255,144,21,0.2)",
                  },
                  /* Accent stripe on left edge */
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    left: 0,
                    top: "12%",
                    bottom: "12%",
                    width: "3px",
                    borderRadius: "0 3px 3px 0",
                    background: accentColor,
                    transition: TRANSITION_SMOOTH,
                    opacity: 0.8,
                  },
                }}
              >
                <CardActionArea
                  onClick={() => onCardTap(item)}
                  sx={{
                    p: 0,
                    "&:hover .MuiCardActionArea-focusHighlight": {
                      opacity: 0.03,
                    },
                  }}
                >
                  <CardContent sx={{ p: 2, pl: 2.5, "&:last-child": { pb: 2 } }}>
                    {/* Top row: name + status */}
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        justifyContent: "space-between",
                        mb: 1,
                        gap: 1,
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          minWidth: 0,
                          flex: 1,
                        }}
                      >
                        {/* Avatar — glassmorphic with gradient border */}
                        <Box
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: "12px",
                            background:
                              "linear-gradient(135deg, rgba(0,40,128,0.12), rgba(0,103,184,0.18))",
                            color: "rgb(0,40,128)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontWeight: 700,
                            fontSize: "0.82rem",
                            letterSpacing: "0.02em",
                            flexShrink: 0,
                            border: "1.5px solid rgba(0,40,128,0.1)",
                            boxShadow:
                              "0 2px 8px rgba(0,40,128,0.08), inset 0 1px 2px rgba(255,255,255,0.5)",
                            transition: TRANSITION_SMOOTH,
                          }}
                        >
                          {(item.prenom?.[0] || "").toUpperCase()}
                          {(item.nom?.[0] || "").toUpperCase()}
                        </Box>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 600,
                              fontSize: "0.9rem",
                              lineHeight: 1.3,
                              letterSpacing: "-0.015em",
                              color: "rgb(0,40,128)",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {item.prenom} {item.nom}
                          </Typography>
                          {item.client && (
                            <Typography
                              variant="caption"
                              sx={{
                                fontSize: "0.73rem",
                                color: "rgba(0,40,128,0.5)",
                                fontWeight: 400,
                                letterSpacing: "0.01em",
                                display: "block",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {item.client}
                            </Typography>
                          )}
                        </Box>
                      </Box>

                      {/* Status chip — premium pill with subtle gradient bg */}
                      {item.status && (
                        <Chip
                          label={<Langs str={item.status} />}
                          size="small"
                          color={chipColor}
                          sx={{
                            fontWeight: 600,
                            fontSize: "0.68rem",
                            height: 26,
                            borderRadius: "10px",
                            letterSpacing: "0.02em",
                            boxShadow: `0 2px 6px ${accentColor.replace(
                              "0.6",
                              "0.2"
                            )}`,
                            transition: TRANSITION_SMOOTH,
                            flexShrink: 0,
                          }}
                        />
                      )}
                    </Box>

                    {/* Info chips row — with refined spacing */}
                    <Box
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 0.75,
                        mt: 0.75,
                      }}
                    >
                      {/* Type chip */}
                      {item.type && (
                        <Chip
                          label={<Langs str={item.type} />}
                          size="small"
                          color={getChipColor(item.type)}
                          variant="outlined"
                          sx={{
                            fontSize: "0.68rem",
                            height: 26,
                            borderRadius: "9px",
                            fontWeight: 500,
                            letterSpacing: "0.01em",
                            transition: TRANSITION_SMOOTH,
                            borderWidth: "1.5px",
                            "& .MuiChip-label": { px: 1.25 },
                          }}
                        />
                      )}

                      {/* Team chip — tappable with special styling */}
                      {item.current_team_name && (
                        <Chip
                          label={item.current_team_name}
                          size="small"
                          variant="outlined"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (onTeamTap) onTeamTap(item);
                          }}
                          sx={{
                            fontSize: "0.68rem",
                            height: 26,
                            maxWidth: 160,
                            borderRadius: "9px",
                            fontWeight: 500,
                            letterSpacing: "0.01em",
                            transition: TRANSITION_SMOOTH,
                            cursor: "pointer",
                            color: "rgb(0,103,184)",
                            borderColor: "rgba(0,103,184,0.35)",
                            borderWidth: "1.5px",
                            bgcolor: "rgba(0,103,184,0.04)",
                            "& .MuiChip-label": { px: 1.25 },
                            "&:hover": {
                              backgroundColor: "rgba(0,103,184,0.1)",
                              borderColor: "rgba(0,103,184,0.5)",
                              transform: "scale(1.03)",
                              boxShadow: "0 2px 8px rgba(0,103,184,0.12)",
                            },
                            "&:active": {
                              transform: "scale(0.96)",
                            },
                          }}
                        />
                      )}

                      {/* Week badge — distinctive pill design */}
                      <Chip
                        icon={
                          <CalendarTodayRoundedIcon
                            sx={{
                              fontSize: "0.72rem !important",
                              color: "rgba(255,144,21,0.7) !important",
                            }}
                          />
                        }
                        label={`S${parseInt(item.week) + 1}`}
                        size="small"
                        sx={{
                          fontSize: "0.68rem",
                          height: 26,
                          borderRadius: "9px",
                          fontWeight: 600,
                          letterSpacing: "0.02em",
                          bgcolor: "rgba(255,144,21,0.08)",
                          color: "rgb(200,110,0)",
                          border: "1.5px solid rgba(255,144,21,0.2)",
                          transition: TRANSITION_SMOOTH,
                          "& .MuiChip-label": { px: 0.75, pr: 1.25 },
                          "& .MuiChip-icon": { ml: 0.75 },
                        }}
                      />
                    </Box>

                    {/* Comments preview — elegant card-in-card */}
                    {item.comments && (
                      <Box
                        sx={{
                          mt: 1.25,
                          p: 1.25,
                          pl: 1.5,
                          borderRadius: "12px",
                          bgcolor: "rgba(0,40,128,0.025)",
                          border: "1px solid rgba(0,40,128,0.06)",
                          display: "flex",
                          gap: 1,
                          alignItems: "flex-start",
                          transition: TRANSITION_SMOOTH,
                        }}
                      >
                        <ChatBubbleOutlineRoundedIcon
                          sx={{
                            fontSize: "0.85rem",
                            color: "rgba(0,40,128,0.25)",
                            mt: "1px",
                            flexShrink: 0,
                          }}
                        />
                        <Typography
                          variant="caption"
                          sx={{
                            fontSize: "0.73rem",
                            color: "rgba(0,40,128,0.55)",
                            fontWeight: 400,
                            lineHeight: 1.5,
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            fontStyle: "italic",
                          }}
                        >
                          {item.comments}
                        </Typography>
                      </Box>
                    )}

                    {/* Footer: created by + date — refined with icons */}
                    <Box
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 2,
                        mt: 1.25,
                        pt: 1,
                        borderTop: "1px solid rgba(0,40,128,0.04)",
                        alignItems: "center",
                      }}
                    >
                      {item.createdBy && (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                          }}
                        >
                          <PersonOutlineRoundedIcon
                            sx={{
                              fontSize: "0.82rem",
                              color: "rgba(0,40,128,0.3)",
                            }}
                          />
                          <Typography
                            variant="caption"
                            sx={{
                              fontSize: "0.72rem",
                              color: "rgba(0,40,128,0.45)",
                              fontWeight: 400,
                            }}
                          >
                            {item.createdBy}
                          </Typography>
                        </Box>
                      )}
                      {item.created_at && (
                        <Typography
                          variant="caption"
                          sx={{
                            fontSize: "0.7rem",
                            color: "rgba(0,40,128,0.35)",
                            fontWeight: 400,
                            letterSpacing: "0.01em",
                            ml: "auto",
                          }}
                        >
                          {formatDate(item.created_at)}
                        </Typography>
                      )}
                    </Box>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Box>
          );
        })}

        {/* Empty state — refined */}
        {paginatedData.length === 0 && (
          <Fade in timeout={400}>
            <Box
              sx={{
                textAlign: "center",
                py: 10,
                animation: `${fadeInScale} 0.5s cubic-bezier(0, 0, 0.2, 1) both`,
              }}
            >
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: "16px",
                  bgcolor: "rgba(0,40,128,0.05)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mx: "auto",
                  mb: 2,
                }}
              >
                <SearchIcon
                  sx={{
                    fontSize: "1.5rem",
                    color: "rgba(0,40,128,0.2)",
                  }}
                />
              </Box>
              <Typography
                variant="body2"
                sx={{
                  color: "rgba(0,40,128,0.35)",
                  fontWeight: 400,
                  fontSize: "0.88rem",
                }}
              >
                <Langs str="No results" />
              </Typography>
            </Box>
          </Fade>
        )}
      </Box>

      {/* Pagination */}
      {totalPages > 1 && (
        <Box sx={{ display: "flex", justifyContent: "center", pt: 1.5 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_e, value) => setPage(value)}
            size="small"
            color="warning"
            sx={{
              "& .MuiPaginationItem-root": {
                borderRadius: "10px",
                transition: TRANSITION_SMOOTH,
                "&:hover": { transform: "scale(1.08)" },
                "&:active": { transform: "scale(0.95)" },
                "&.Mui-selected": {
                  boxShadow: "0 2px 8px rgba(255,144,21,0.35)",
                },
              },
            }}
          />
        </Box>
      )}
    </Box>
  );
}
